<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: BioDataCard                                           |
| Section: components                                              |
| Purpose: Displays Bio Data details in a card layout.             |
+------------------------------------------------------------------+
-->
<template>
    <div class="BioDataCard_container wrapper">
        <div :class="['BioDataCard wrapper', { 'BioDataCard--reverse': reverse, 'BioDataCard--no-photo': !photoSrc }]"
            ref="BioDataCard">
            <div :class="['text_container', { 'text_container--reverse': reverse, 'text_container--no-photo': !photoSrc }]"
                ref="textContainer">
                <div class="text_wrapper">
                    <!-- Use props for dynamic title and label -->
                    <TitleGroup :label="label" :title="title" />
                    <!-- Iterate over bioDataGroups and display them -->
                    <div v-for="(group, index) in bioDataGroups" :key="index" class="BioDataGroup">
                        <!-- Collapsible header for 'Adressen' -->
                        <template v-if="isAddressGroup(group) && addressCount(group) > 0">
                            <button class="collapsible_header" type="button"
                                @click="toggleGroup(groupKey(group, index))"
                                :aria-expanded="isExpanded(groupKey(group, index))"
                                :aria-controls="`addresses-panel-${index}`">
                                <h3>{{ $t('bioDataCard.addresses') }}</h3>
                                <span class="count_badge">{{ addressCount(group) }}</span>
                                <svg class="chevron" viewBox="0 0 24 24" aria-hidden="true"
                                    :class="{ rotated: isExpanded(groupKey(group, index)) }">
                                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                                </svg>
                            </button>

                            <transition @before-enter="beforeEnter" @enter="enter" @after-enter="afterEnter"
                                @before-leave="beforeLeave" @leave="leave" @after-leave="afterLeave">
                                <!-- Collapsible content -->
                                <div :id="`addresses-panel-${index}`" class="collapsible_panel"
                                    v-show="isExpanded(groupKey(group, index))" @transitionend="setDynamicPadding">
                                    <!-- Keep rendering exactly as before -->
                                    <h3 class="sr-only">{{ $t('bioDataCard.addresses') }}</h3>

                                    <table v-if="group.format === 'table'">
                                        <tbody>
                                            <tr v-for="(item, i) in group.items" :key="i">
                                                <td class="capitalize-first-letter">{{ item.date }}</td>
                                                <td>{{ item.text }}</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <table v-else-if="group.format === 'alias'">
                                        <tbody>
                                            <tr v-for="(item, i) in group.items" :key="i">
                                                <td class="alias_table_data" v-html="item.text"></td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <p v-else v-for="(item, i) in group.items" :key="i">
                                        <img :src="getIconPath(item.icon)" v-if="item.icon" alt="icon">
                                        <span class="capitalize-first-letter" v-if="item.isHtml"
                                            v-html="item.text"></span>
                                        <span class="capitalize-first-letter" v-else>{{ item.text }}</span>
                                    </p>

                                    <div v-if="groupedNotes[group.eventID]?.length > 0">
                                        <p class="note" v-for="(note, noteIndex) in groupedNotes[group.eventID]"
                                            :key="noteIndex">
                                            <img class="note-icon" src="@/assets/icons/feather.svg" alt="icon">
                                            <span v-html="note"></span>
                                        </p>
                                    </div>

                                    <div v-if="group.images && group.images.length > 0" class="attached_images">
                                        <a v-for="(image, imgIndex) in group.images" :key="imgIndex"
                                            @mouseenter="showTooltip(image.description)" @mouseleave="hideTooltip"
                                            @click.prevent="authToken ? handleImageClick(image, imgIndex, group.images) : $router.push('/login')"
                                            class="attached_image"
                                            :style="{ backgroundImage: getCustomThumbnailBackgroundImage(image) }"
                                            :title="image.description">
                                        </a>
                                    </div>
                                </div>
                            </transition>
                        </template>

                        <!-- Non-address groups render unchanged -->
                        <template v-else>
                            <h3>{{ group.title }}</h3>

                            <table v-if="group.format === 'table'">
                                <tbody>
                                    <tr v-for="(item, i) in group.items" :key="i">
                                        <td class="capitalize-first-letter">{{ item.date }}</td>
                                        <td>{{ item.text }}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <table v-else-if="group.format === 'alias'">
                                <tbody>
                                    <tr v-for="(item, i) in group.items" :key="i">
                                        <td class="alias_table_data" v-html="item.text"></td>
                                    </tr>
                                </tbody>
                            </table>

                            <p v-else v-for="(item, i) in group.items" :key="i">
                                <img :src="getIconPath(item.icon)" v-if="item.icon" alt="icon">
                                <span class="capitalize-first-letter" v-if="item.isHtml" v-html="item.text"></span>
                                <span class="capitalize-first-letter" v-else>{{ item.text }}</span>
                            </p>

                            <div v-if="groupedNotes[group.eventID]?.length > 0">
                                <p class="note" v-for="(note, noteIndex) in groupedNotes[group.eventID]"
                                    :key="noteIndex">
                                    <img class="note-icon" src="@/assets/icons/feather.svg" alt="icon">
                                    <span v-html="note"></span>
                                </p>
                            </div>

                            <div v-if="group.images && group.images.length > 0" class="attached_images">
                                <a v-for="(image, imgIndex) in group.images" :key="imgIndex"
                                    @mouseenter="showTooltip(image.description)" @mouseleave="hideTooltip"
                                    @click.prevent="authToken ? handleImageClick(image, imgIndex, group.images) : $router.push('/login')"
                                    class="attached_image"
                                    :style="{ backgroundImage: getCustomThumbnailBackgroundImage(image) }"
                                    :title="image.description">
                                </a>
                            </div>
                        </template>
                    </div>


                    <!-- <div class="BioDataGroup">
                        <h3>Ook bekend als</h3>
                        <table>
                            <tbody>
                                <tr>
                                    <td></td>
                                    <td>De Christenvrouw Catharina, blijkens de geboorteakte van dochter Anna Paulina en
                                        zoon Josephus </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td>De Javaansche vrouw Kiem of Jiem, de Javaansche vrouw Siem en de Chineesche
                                        vrouw Sim (verbastering van "Kin"), blijkens de huwelijksakte van zoon Willem
                                        Jan Manuel alsmede de doopaktes van Willem Jan voornoemd en Maria Louisa Manuel
                                        -> duidt op Peranakan Chineesche afkomst, wat inhoudt dat haar Chineesche
                                        (bet)(over)(groot)ouders zich reeds hebben vermengd met de inheemse bevolking
                                        van de Oost-Indische archipel. </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td>Sienjo, blijkens de geboorteaktes van haar kinderen Adolphina Christina en
                                        Adolph Christiaan Auliveld en blijkens de overlijdensakte van haar man Jean
                                        Pierre Auliveld. Dit is waarschijnlijk de meest natuurgetrouwe wijze van het
                                        opschrijven van de uitspraak van haar eigen namen Kin Nio, voor zover dat
                                        mogelijk is met Latijns schrift.</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td>Sikin, blijkens de Rijksalmanak en de doopakte</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td>陳金[...] -> Gezien alle leden van de familie Tan op Java die voorkomen in "The
                                        New Commercial Directory of The Dutch East Indies of 1932 by Nam Tiong Boek
                                        Handel Batavia" hun familienaam schreven als "陳", was dit waarschijnlijk óók de
                                        oorspronkelijke schrijfwijze van de familienaam van onze Tan Sienyo. Volgens My
                                        China Roots zou de naam een eerbetoon zijn aan Chen Hugong—de eerste drager van
                                        deze familienaam alsmede de oprichter van de staat Chen. Betreft haar
                                        generatienaam Kin of Kim, werd dit waarschijnlijk geschreven als "金" omwille van
                                        dezelfde redenen en het betekend letterlijk "goud". Het karakter voor haar eigen
                                        naam (Nio) heb ik nog niet kunnen achterhalen. </td>
                                </tr>
                            </tbody>
                        </table>
                    </div> -->
                </div>
                <!-- Render Marriage Data in the BioDataCard -->
                <div v-if="marriageData.length > 0" class="MarriageDataGroup">
                    <h3>{{ marriageSectionTitle }}</h3>
                    <div v-for="(marriage, index) in marriageData" :key="index" class="MarriageDataText">
                        <p>
                            <img src="@/assets/icons/heart-red.svg" alt="heart">
                            <!-- <span class="capitalize-first-letter" style="color: var(--brand-color-1);"><strong>{{
                                marriage.spouseName }}</strong></span> -->
                            <a :href="marriage.spouseID ? localePath(`/person/${marriage.spouseID}`) : 'javascript:void(0)'"
                                class="spouse-link capitalize-first-letter" style="color: var(--brand-color-1);">
                                <strong>{{ marriage.spouseName }}</strong>
                            </a>
                        </p>
                        <p v-if="marriage.date">
                            <img src="@/assets/icons/date.svg" alt="date">
                            <span class="capitalize-first-letter">{{ marriage.date }}</span>
                        </p>
                        <p v-if="marriage.place">
                            <img src="@/assets/icons/location.svg" alt="location">
                            {{ marriage.place }}
                        </p>
                        <p class="note" v-for="(note, noteIndex) in marriage.marriageNotes || []"
                            :key="note.xnoteID || `marriage-note-${index}-${noteIndex}`">
                            <img class="note-icon" src="@/assets/icons/feather.svg" alt="icon">
                            <span v-html="marriageNoteText(note)"></span>
                        </p>
                        <!-- Render attached images -->
                        <div class="attached_images" v-if="marriage.documents && marriage.documents.length > 0">
                            <a v-for="(document, docIndex) in marriage.documents" :key="document.mediaID"
                                @mouseenter="showTooltip(document.description)" @mouseleave="hideTooltip"
                                @click.prevent="authToken ? handleImageClick(document, docIndex, marriage.documents) : $router.push('/login')"
                                class="attached_image"
                                :style="{ backgroundImage: getCustomThumbnailBackgroundImage(document) }"
                                :title="document.description">
                                <!-- Tooltip container -->
                                <!-- <span class="tooltip" v-if="tooltip.visible && tooltip.text === document.description">
                                    {{ tooltip.text }}
                                </span> -->
                            </a>
                        </div>
                    </div>
                </div>

                <div v-if="normalizedGeneralNotes.length > 0" class="GeneralNotesGroup">
                    <h3>{{ $t('bioDataCard.notes') }}</h3>
                    <div class="GeneralNotesList">
                        <p v-for="(note, index) in normalizedGeneralNotes" :key="`general-note-${index}`"
                            class="GeneralNote">
                            <span>{{ note }}</span>
                        </p>
                    </div>
                </div>
            </div>

            <!-- Render the photo_container when there is no map to show -->
            <div v-if="shouldShowPhotoContainer"
                :class="['photo_container', {
                    'photo_container--reverse': reverse,
                    'photo_container--event-map-fallback': eventMapHasNoLocations,
                    'default-photo': shouldUseDefaultPhotoContainer
                }]"
                :style="{ backgroundImage: photoContainerBackgroundImage }">
                <!-- Photo container content -->
                <a v-if="showPhotosButton" :href="`/fotos-en-documenten?type=photos&search=${personID}`">
                    <img src="@/assets/icons/photos.svg" :alt="formattedTitle">{{ formattedTitle }}
                </a>
            </div>

            <!-- Render the EventMap if showEventMap is true -->
            <EventMap v-if="showEventMap && !eventMapHasNoLocations" :key="personID" :personID="personID"
                @empty-state-change="handleEventMapEmptyStateChange" />

        </div>
    </div>
</template>

<script>
import TitleGroup from '@/atoms/TitleGroup.vue';
import EventMap from './EventMap.vue';
import { nextTick } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { handleScroll } from '@/utils/scrollUtils';
import { getBackgroundImageUrl, getDocumentPath, getDocumentThumbnailPath, normalizeMediaUrl } from '@/utils/mediaUtils';
import { debugLog } from '@/utils/logger';

export default {
    components: {
        TitleGroup,
        EventMap
    },
    setup() {
        // Injecting properties from App.vue using the useAuth composable
        const { authToken, username, logout } = useAuth();
        const { localePath } = useLocale();

        return {
            authToken,
            username,
            logout,
            localePath,
        };
    },
    data() {
        return {
            tooltip: {
                visible: false,
                text: ''
            },
            expandedGroups: {}, // track open/closed per group key
            eventMapHasNoLocations: false
        };
    },
    props: {
        reverse: {
            type: Boolean,
            default: false
        },
        bioDataGroups: {
            type: Array,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        label: {
            type: String,
            required: true
        },
        photoSrc: {
            type: String,
            default: ''
        },
        personID: {
            type: String,
            required: true
        },
        showPhotosButton: {
            type: Boolean,
            required: true
        },
        showEventMap: {
            type: Boolean,
            default: false
        },
        gender: {
            type: String,
            default: ''
        },
        openMediaOverlay: {
            type: Function,
            required: true
        },
        marriageData: {
            type: Array,
            default: () => [] // Default to an empty array if no marriage data is passed
        },
        notes: {
            type: Array,
            default: () => [] // Default to an empty array
        },
        generalNotes: {
            type: Array,
            default: () => []
        }
    },
    mounted() {
        this.handleScroll = () => handleScroll({
            textContainer: this.$refs.textContainer,
            element: this.$refs.BioDataCard // or BioDataCard for BioDataCard component
        });

        // Call handleScroll on mount to set the correct position immediately
        this.handleScroll();

        window.addEventListener('scroll', this.handleScroll);

        this.setDynamicPadding();
        window.addEventListener('resize', this.setDynamicPadding);
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.setDynamicPadding);
    },
    watch: {
        bioDataGroups: {
            deep: true,
            handler() {
                // Wait for the DOM to update and then set the padding
                nextTick(() => {
                    this.setDynamicPadding();
                });
            }
        },
        marriageData: {
            deep: true,
            handler() {
                nextTick(() => this.setDynamicPadding());
            }
        },
        generalNotes: {
            deep: true,
            handler() {
                nextTick(() => this.setDynamicPadding());
            }
        },
        expandedGroups: {
            deep: true,
            handler() {
                nextTick(() => this.setDynamicPadding());
            }
        },
        personID() {
            this.eventMapHasNoLocations = false;
        },
        showEventMap() {
            this.eventMapHasNoLocations = false;
        },
    },
    computed: {
        // Computed property to correctly encode the URL path segments without double encoding
        encodedPhotoSrc() {
            return normalizeMediaUrl(this.photoSrc);
        },
        photoContainerBackgroundImage() {
            return getBackgroundImageUrl(this.encodedPhotoSrc);
        },
        formattedTitle() {
            const sex = this.gender;
            if (sex === 'M') return this.$t('bioDataCard.viewPhotos.male');
            if (sex === 'F') return this.$t('bioDataCard.viewPhotos.female');
            return this.$t('bioDataCard.viewPhotos.unknown');
        },
        shouldHidePhotoContainer() {
            const isDefaultPhoto = this.photoSrc === '/img/sejarah_thumann1080x781.4fc54a6a.jpg'; // Replace with your actual default photo path
            return !this.photoSrc || isDefaultPhoto;
        },
        shouldShowPhotoContainer() {
            return !this.showEventMap || this.eventMapHasNoLocations;
        },
        shouldUseDefaultPhotoContainer() {
            return this.eventMapHasNoLocations || this.shouldHidePhotoContainer;
        },
        marriageSectionTitle() {
            const marriages = Array.isArray(this.marriageData) ? this.marriageData : [];
            const isPlural = marriages.length > 1;
            const hasKnownMarriageDetails = marriages.some((marriage) => {
                const hasDate = !!String(marriage?.date ?? '').trim();
                const hasPlace = !!String(marriage?.place ?? '').trim();
                return hasDate || hasPlace;
            });

            if (hasKnownMarriageDetails) {
                return isPlural ? this.$t('bioDataCard.marriage.plural') : this.$t('bioDataCard.marriage.single');
            }

            return isPlural ? this.$t('bioDataCard.marriage.relationPlural') : this.$t('bioDataCard.marriage.relationSingle');
        },
        groupedNotes() {
            const notesArray = Array.isArray(this.notes) ? this.notes : []; // Ensure it's always an array
            return notesArray.reduce((acc, note) => {
                if (!acc[note.eventID]) acc[note.eventID] = [];
                acc[note.eventID].push(note.note);
                return acc;
            }, {});
        },
        normalizedGeneralNotes() {
            return (Array.isArray(this.generalNotes) ? this.generalNotes : [])
                .map(note => this.generalNoteText(note))
                .filter(Boolean);
        },

    },
    methods: {

        marriageNoteText(note) {
            if (note === null || note === undefined) return '';
            return String(note.note ?? note);
        },

        generalNoteText(note) {
            if (note === null || note === undefined) return '';

            const value = note.note || note.noteText || note.text || note.notes || note;
            return String(value)
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<[^>]+>/g, '')
                .trim();
        },

        // Helper: animate padding by a delta (positive for open, negative for close)
        handleEventMapEmptyStateChange(isEmpty) {
            this.eventMapHasNoLocations = isEmpty;
            nextTick(() => this.setDynamicPadding());
        },
        adjustCardPaddingBy(deltaPx) {
            const card = this.$refs.BioDataCard;
            if (!card) return;

            // If no-photo variant is active, bail (it forces a fixed padding with !important)
            if (card.classList.contains('BioDataCard--no-photo')) return;

            const current = parseFloat(getComputedStyle(card).paddingBottom) || 0;
            const next = Math.max(0, current + deltaPx);
            card.style.paddingBottom = `${next}px`; // will animate because of CSS transition above
        },

        /* ===== Option B hooks (auto-height panel) ===== */

        // OPEN
        beforeEnter(el) {
            // prepare panel
            el.style.height = '0px';
            el.style.opacity = '0';
            el.style.overflow = 'hidden';

            // compute how much height will be added and animate padding by the same delta
            const delta = el.scrollHeight; // final panel height
            this.adjustCardPaddingBy(delta);
        },
        enter(el, done) {
            void el.offsetHeight; // reflow
            el.style.transition = 'height .28s ease, opacity .2s ease';
            el.style.height = el.scrollHeight + 'px';
            el.style.opacity = '1';
            const cleanup = () => { el.removeEventListener('transitionend', cleanup); done(); };
            el.addEventListener('transitionend', cleanup);
        },
        afterEnter(el) {
            el.style.transition = '';
            el.style.height = 'auto';
            // Snap to exact final padding in case of rounding (usually no change):
            this.$nextTick(() => this.setDynamicPadding());
        },

        // CLOSE
        beforeLeave(el) {
            el.style.height = el.scrollHeight + 'px';
            el.style.opacity = '1';
            el.style.overflow = 'hidden';

            // animate padding down by the panel’s current height
            const delta = -el.scrollHeight;
            this.adjustCardPaddingBy(delta);
        },
        leave(el, done) {
            void el.offsetHeight; // reflow
            el.style.transition = 'height .24s ease, opacity .18s ease';
            el.style.height = '0px';
            el.style.opacity = '0';
            const cleanup = () => { el.removeEventListener('transitionend', cleanup); done(); };
            el.addEventListener('transitionend', cleanup);
        },
        afterLeave() {
            // Ensure precision after close
            this.$nextTick(() => this.setDynamicPadding());
        },

        isAddressGroup(group) {
            // Use eventID for locale-independent detection
            return group?.eventID === 'ADDR';
        },
        addressCount(group) {
            // Count only items that actually have visible text
            const items = Array.isArray(group?.items) ? group.items : [];
            return items.filter(i => (i?.text && String(i.text).trim().length)).length;
        },
        groupKey(group, index) {
            // Prefer a stable key (eventID), fallback to title+index
            return group?.eventID ?? `${(group?.title || 'group')}-${index}`;
        },
        isExpanded(key) {
            // Default: collapsed (false)
            return !!this.expandedGroups[key];
        },
        toggleGroup(key) {
            // flip state
            this.$set
                ? this.$set(this.expandedGroups, key, !this.expandedGroups[key])
                : (this.expandedGroups = { ...this.expandedGroups, [key]: !this.expandedGroups[key] });

            // recalc after DOM updates
            nextTick(() => this.setDynamicPadding());
        },
        // handleScroll() {
        //     const textContainer = this.$refs.textContainer;
        //     const storyCard = this.$refs.storyCard;

        //     if (textContainer && storyCard) {
        //         const storyCardRect = storyCard.getBoundingClientRect();
        //         const scrollPosition = window.scrollY || window.pageYOffset;
        //         const elementTop = storyCardRect.top + scrollPosition;
        //         const windowHeight = window.innerHeight;

        //         const maxTop = 192; // Max top value
        //         const minTop = -96;   // Min top value

        //         // Calculate how much of the card is in view
        //         const scrollFraction = (scrollPosition + windowHeight - elementTop) / (windowHeight + storyCard.offsetHeight);

        //         // Clamp the scrollFraction between 0 and 1
        //         const clampedScrollFraction = Math.max(0, Math.min(1, scrollFraction));

        //         // Apply a gentler easing function to make the movement slower in the middle
        //         const easedScrollFraction = this.gentleEaseOutIn(clampedScrollFraction);

        //         // Calculate the new top value using the eased fraction
        //         const newTop = maxTop - (maxTop - minTop) * easedScrollFraction;

        //         // Apply the new top value immediately when page loads or on scroll
        //         textContainer.style.top = `${newTop}px`;
        //     }
        // },
        showTooltip(text) {
            this.tooltip.visible = true;
            this.tooltip.text = text;
        },
        hideTooltip() {
            this.tooltip.visible = false;
            this.tooltip.text = '';
        },
        handleImageClick(image, index, mediaArray) {
            debugLog('Clicked Image:', { image, index, mediaArray });

            const mediaItem = {
                ...image,
                path: this.getCustomFilePath(image),
                thumbpath: this.getCustomThumbnailPath(image),
                linkedPersonIDs: image.linkedPersonIDs || [], // Ensure linkedPersonIDs are passed
            };

            if (this.openMediaOverlay && typeof this.openMediaOverlay === 'function') {
                // Pass the correct subset (group.images) and index
                this.openMediaOverlay(mediaItem, index, mediaArray);
            } else {
                console.error('openMediaOverlay function not provided or is not a function');
            }
        },
        setDynamicPadding() {
            const textContainer = this.$refs.textContainer;
            const BioDataCard = this.$refs.BioDataCard;
            if (textContainer && BioDataCard) {
                const textContainerHeight = textContainer.offsetHeight;
                let paddingBottom = 0;

                if (textContainerHeight > 404) {
                    paddingBottom = textContainerHeight - 404;
                }

                BioDataCard.style.paddingBottom = `${paddingBottom}px`;
            }
        },
        getCustomThumbnailPath(image) {
            if (image && image.thumbpath) {
                return getDocumentThumbnailPath(image.thumbpath);
            }
            return 'path/to/default-thumbnail.jpg'; // Fallback path for missing thumbnails
        },
        getCustomThumbnailBackgroundImage(image) {
            const thumbnailPath = this.authToken
                ? this.getCustomThumbnailPath(image)
                : 'path/to/guest-thumbnail.jpg';

            return getBackgroundImageUrl(thumbnailPath);
        },
        getCustomFilePath(image) {
            if (image && image.path) {
                return getDocumentPath(image.path);
            }
            return 'path/to/default-file.jpg'; // Fallback path for missing files
        },
        getIconPath(icon) {
            // Use require to correctly resolve the icon path, with a relative path
            return require(`@/assets/icons/${icon}`);
        },
        emitOpenGalleryOverlay() {
            // Emit the event to open the gallery overlay
            this.$emit('open-gallery-overlay');
        },
    }
}
</script>

<style scoped>
.BioDataCard_container {
    padding: var(--page-padding);
}

.BioDataCard {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
}

.BioDataCard--reverse {
    justify-content: flex-start;
}

.BioDataCard--no-photo {
    padding-bottom: 96px !important;
}

.text_container {
    display: flex;
    width: 100%;
    max-width: calc(50% + 48px);
    /* padding: 48px; */
    flex-direction: column;
    align-items: flex-start;
    background: var(--brand-bg-1);
    /* box-shadow: var(--shadow-card-lg); */
    position: absolute;
    left: 0;
    top: 96px;
    z-index: 2;
}

.text_container--reverse {
    right: 0;
    left: auto;
}

.text_container--no-photo {
    position: static !important;
    top: 0 !important;
    max-width: none;
}

.text_wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 36px;
    align-self: stretch;
}

.BioDataGroup {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
}

.BioDataGroup p {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
}

.BioDataGroup img {
    margin-top: 3.5px;
}

.spouse-link:hover {
    text-decoration: underline;
}

.note,
.note span {
    width: 100%;
}

.note-icon {
    margin-top: 5px !important;
}

table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
}

th {
    font-weight: 600;
}

tr {
    display: flex;
    padding: 12px 0px;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
    border-top: 1px solid #EEE;
    border-bottom: 1px solid #EEE;
}

th:nth-of-type(1),
td:nth-of-type(1) {
    width: 24%;
    text-align: left;
}

th:nth-of-type(2),
td:nth-of-type(2) {
    width: 76%;
    text-align: left;
}

.photo_container {
    background-position: center;
    background-size: contain;
    background-color: var(--brand-bg-5);
    background-repeat: no-repeat;
    width: 100%;
    max-width: calc(50% + 48px);
    height: 500px;
    position: relative;
    z-index: 1;
    border-radius: var(--border-radius-sm);
}

.photo_container a {
    display: flex;
    padding: 12px 24px;
    align-items: center;
    gap: 8px;
    position: absolute;
    right: 24px;
    bottom: 24px;
    border-radius: var(--border-radius-pill);
    background: var(--brand-bg-7);
    font-weight: 600;
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px var(--brand-bg-7);
}

.photo_container--reverse a {
    right: auto;
    left: 24px;
}

.photo_container a:hover {
    box-shadow: 0px 0px 0px 4px var(--brand-bg-7);
    text-decoration: underline;
}

.MarriageDataGroup {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    align-self: stretch;
}

.MarriageDataGroup img {
    margin-top: 3px;
}

.MarriageDataText {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 0px;
    border-top: 1px solid #EEE;
    border-bottom: 1px solid #EEE;
    width: 100%;
}

.MarriageDataText:not(:last-of-type) {
    margin-bottom: -8px;
}

.MarriageDataText p {
    color: var(--brand-text-dark);
    display: flex;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    margin-bottom: 0;
}

.GeneralNotesGroup {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
}

.GeneralNotesList {
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-self: stretch;
}

.GeneralNote {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    margin-bottom: 0;
}

.GeneralNote span {
    width: 100%;
    white-space: pre-line;
}

.alias_table_data {
    width: 100% !important;
    /* display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal; */
}

.attached_images a {
    position: relative;
}

.tooltip {
    top: calc(-100% - 6px);
    background-color: var(--brand-bg-4);
    color: var(--brand-text-light);
}

.tooltip::before,
.tooltip::after {
    content: '';
    height: 8px;
    width: 8px;
    position: absolute;
    bottom: -8px;
}

.tooltip::before {
    left: 50%;
    background: linear-gradient(45deg, rgba(29, 29, 29, 0) 50%, var(--brand-bg-4) 50%);
    transform: translateX(-50%);
}

.tooltip::after {
    left: 50%;
    background: linear-gradient(-45deg, rgba(29, 29, 29, 0) 50%, var(--brand-bg-4) 50%);
    transform: translateX(50%);
}

.collapsible_header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
}

.collapsible_header:hover>h3 {
    text-decoration: underline;
}

.collapsible_header h3 {
    margin: 0;
}

.count_badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    background: var(--brand-bg-7);
    color: var(--brand-text-dark);
}

.chevron {
    margin-left: auto;
    width: 24px;
    height: 24px;
    padding-left: 1px;
    transition: transform .2s ease;
    fill: #FFF;
    border-radius: 9999px;
    background: var(--brand-color-1);

    /* opacity: .8; */
}

.chevron.rotated {
    transform: rotate(90deg);
}

.collapsible_panel {
    margin-top: 8px;
}

.chevron {
    transition: transform .2s ease;
}

.chevron.rotated {
    transform: rotate(90deg);
}


.collapsible_panel {
    will-change: height;
}

@media (prefers-reduced-motion: reduce) {
    .collapsible_panel {
        transition: none !important;
    }
}

/* Screen-reader only header to keep semantic structure without double heading */
.sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    border: 0 !important;
}

.BioDataCard {
    transition: padding-bottom .28s ease;
    /* matches your enter timing */
}


@media (max-width: 860px) {
    .BioDataCard_container {
        padding: var(--page-padding-mobile);
    }

    .BioDataCard {
        flex-direction: column !important;
        padding-bottom: 0 !important;
        gap: 72px;
    }

    .text_container {
        position: static;
        max-width: 100%;
        margin: 0 auto;
        gap: 20px;
        padding: 24px;
    }

    .text_wrapper {
        gap: 16px;
    }

    .photo_container,
    .photo_container--reverse {
        padding-bottom: 81%;
        max-width: calc(100% + 40px);
        width: calc(100% + 40px);
        margin-left: -20px;
        height: auto;
    }

    .photo_container.default-photo {
        display: none;
    }

    .photo_container.default-photo.photo_container--event-map-fallback {
        display: block;
    }
}
</style>
